import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { RefreshCw, Percent, Plus } from 'lucide-react';
import { listFeeDefinitions, createFeeDefinition } from '../../../services/yoolpay/feeDefinitionsApi';

// Valores del CHECK de fee_definitions (migration 012): system/company/product
const SCOPE_TYPES = {
  system:  'Sistema (aplica a todo)',
  company: 'Compañía específica',
  product: 'Plan/producto específico',
};

// fee_type que consume el código: fee-resolver.service.ts (recaudacion,
// intermediacion, iva) y settlement-cuadratura.service.ts (merchant/psp)
const FEE_TYPES = {
  recaudacion:    'Recaudación (Yool Digital)',
  intermediacion: 'Intermediación (Yool Broker)',
  iva:            'IVA sobre fees',
  psp:            'Fee PSP (cuadratura)',
  merchant:       'Merchant rate (cuadratura)',
};

const BASES = {
  monto_bruto: 'Monto bruto del cargo',
  prima_neta:  'Prima neta',
  fee_amount:  'Sobre los fees (para IVA)',
};

const PARTES = {
  yool_digital: 'Yool Digital',
  yool_broker:  'Yool Broker',
  compania:     'Compañía',
  psp:          'PSP',
};

const EMPTY_FORM = {
  scope_type: 'system',
  scope_id: '',
  fee_type: 'recaudacion',
  label: '',
  base: 'monto_bruto',
  value_kind: 'rate', // 'rate' (porcentaje) | 'amount' (CLP fijo)
  value: '',
  emisor: 'yool_digital',
  receptor: 'compania',
  valid_from: new Date().toISOString().slice(0, 10),
};

const fmtPct = (v) => (v != null ? `${(Number(v) * 100).toFixed(2)}%` : '—');
const fmtClp = (v) =>
  v != null ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v) : '—';

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export default function YoolPayFeeDefinitionsList() {
  const { getToken } = useAuth();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formOk, setFormOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      setFees(await listFeeDefinitions(token));
    } catch (e) {
      setError(e?.message ?? 'Error al cargar las tasas');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setFormOk(false);
    try {
      if (!form.label.trim()) throw new Error('Falta la etiqueta (nombre visible de la tasa)');
      if (form.scope_type !== 'system' && !form.scope_id.trim()) {
        throw new Error(
          form.scope_type === 'company'
            ? 'Indica el UUID de la compañía a la que aplica esta tasa'
            : 'Indica el UUID del plan/producto al que aplica esta tasa',
        );
      }
      const value = Number(String(form.value).replace(',', '.'));
      if (!form.value || Number.isNaN(value) || value <= 0) {
        throw new Error(form.value_kind === 'rate' ? 'Ingresa el porcentaje (ej: 5 = 5%)' : 'Ingresa el monto fijo en CLP');
      }
      if (form.value_kind === 'rate' && value > 100) throw new Error('El porcentaje no puede superar 100');

      const payload = {
        scope_type: form.scope_type,
        fee_type: form.fee_type,
        label: form.label.trim(),
        base: form.base,
        emisor: form.emisor,
        receptor: form.receptor,
        valid_from: form.valid_from,
      };
      if (form.scope_type !== 'system') payload.scope_id = form.scope_id.trim();
      if (form.value_kind === 'rate') payload.rate = Number((value / 100).toFixed(6));
      else payload.amount_clp = value;

      const token = await getToken();
      await createFeeDefinition(token, payload);
      setForm(EMPTY_FORM);
      setFormOk(true);
      await load();
    } catch (e) {
      setFormError(e?.message ?? 'Error al crear la tasa');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Percent className="h-6 w-6" />
            YoolPay — Tasas de Comisión
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            fee_definitions · las lee resolve_fees para la liquidación · crear desactiva la anterior vigente del mismo scope
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nueva tasa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Tipo de fee">
                <Select value={form.fee_type} onValueChange={setField('fee_type')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FEE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Etiqueta (nombre visible)">
                <Input value={form.label} onChange={(e) => setField('label')(e.target.value)} placeholder="ej: Fee Recaudación Yool Digital" />
              </Field>
              <Field label="Se calcula sobre">
                <Select value={form.base} onValueChange={setField('base')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(BASES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tipo de valor">
                <Select value={form.value_kind} onValueChange={setField('value_kind')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rate">Porcentaje (%)</SelectItem>
                    <SelectItem value="amount">Monto fijo (CLP)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={form.value_kind === 'rate' ? 'Porcentaje (ej: 5 = 5%)' : 'Monto fijo en CLP'}>
                <Input
                  value={form.value}
                  onChange={(e) => setField('value')(e.target.value)}
                  placeholder={form.value_kind === 'rate' ? '5' : '25000'}
                  inputMode="decimal"
                />
              </Field>
              <Field label="Vigente desde">
                <Input type="date" value={form.valid_from} onChange={(e) => setField('valid_from')(e.target.value)} />
              </Field>
              <Field label="Alcance">
                <Select value={form.scope_type} onValueChange={setField('scope_type')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SCOPE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {form.scope_type !== 'system' && (
                <Field label={form.scope_type === 'company' ? 'UUID de la compañía' : 'UUID del plan/producto'}>
                  <Input value={form.scope_id} onChange={(e) => setField('scope_id')(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
                </Field>
              )}
              <Field label="Quién cobra (emisor)">
                <Select value={form.emisor} onValueChange={setField('emisor')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PARTES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="A quién se le cobra (receptor)">
                <Select value={form.receptor} onValueChange={setField('receptor')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PARTES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {formError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2 text-sm text-destructive">
                {formError}
              </div>
            )}
            {formOk && (
              <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-2 text-sm text-emerald-600">
                Tasa creada. Se desactivó la anterior vigente del mismo scope (si existía).
              </div>
            )}

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Crear tasa'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">{fees.length} tasas activas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Etiqueta</TableHead>
                  <TableHead>Base</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Emisor → Receptor</TableHead>
                  <TableHead>Desde</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fees.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Sin tasas activas.
                    </TableCell>
                  </TableRow>
                )}
                {fees.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{f.scope_type}</Badge>
                      {f.scope_id && <span className="ml-1 font-mono text-[10px] text-muted-foreground">{f.scope_id.slice(0, 8)}…</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{f.fee_type}</TableCell>
                    <TableCell className="text-xs">{f.label}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">{f.base}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {f.rate != null ? fmtPct(f.rate) : fmtClp(f.amount_clp)}
                    </TableCell>
                    <TableCell className="font-mono text-[10px]">{f.emisor} → {f.receptor}</TableCell>
                    <TableCell className="font-mono text-xs">{f.valid_from ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
